import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Can } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import {
    downloadLetter,
    useAssemblyBallots,
    useAssemblyPreparation,
    useContractEvents,
    useConvocationMutations,
} from '../../hooks/useBallots'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { apiErrorMessage, useAssemblyAttendance, useAssemblyMutations } from './assembly-api'
import { HelpHint } from './HelpHint'
import { NextAction, StepList, type StepDef } from './StepList'
import { currentStep } from './assembly-utils'

/**
 * The preparation phase of an assembly (spec 2026-10-05-08 §9), in three steps,
 * one open at a time: the preparation (agenda, dates, modes, anomalies), the
 * participation of the remote ballots (who answered, never how) and the journal
 * of the minutes. The next action is one primary button above the steps.
 * Spec 2026-10-10-08 §9.
 */
export function ConvocationTab({ org, minutesUid }: { org: string; minutesUid: string }) {
    const { t } = useTranslation('assemblies')
    const { formatDate } = useFormatters()
    const { data: prep, isLoading } = useAssemblyPreparation(org, minutesUid)
    const convened = !!prep && prep.status !== 'draft'
    const { data: participation } = useAssemblyBallots(org, minutesUid, convened)
    const { data: journal } = useContractEvents(org, minutesUid, convened)
    const { prepare, convene, remind, reissue } = useConvocationMutations()
    const [confirm, setConfirm] = useState(false)
    const [postponing, setPostponing] = useState(false)
    const [newDate, setNewDate] = useState('')
    const [reason, setReason] = useState('')
    const { postpone } = useAssemblyMutations(org)
    // null = follow the step the next action belongs to; undefined = all closed.
    const [chosen, setChosen] = useState<string | undefined | null>(null)
    // The identity_provider level waits on a certified provider contract
    // (FranceConnect+): the API refuses it, so it is never offered here.
    const [identityLevel, setIdentityLevel] = useState<'otp' | 'strong'>('otp')
    // Rightsholders who asked for a paper convocation at this assembly. A
    // contact flagged postal_convocation, or without an e-mail, gets one anyway.
    const { data: sheet } = useAssemblyAttendance(org, minutesUid)
    const [postal, setPostal] = useState<string[]>([])
    const [notice, setNotice] = useState<string | null>(null)
    const [letterError, setLetterError] = useState<string | null>(null)
    const path = { orgCode: org, contractUid: minutesUid }
    const postponeError = apiErrorMessage(postpone.error)
    const error = apiErrorMessage(prepare.error || convene.error || remind.error || reissue.error) || letterError

    if (isLoading || !prep) return <p className="text-sm text-muted-foreground">{t('loading')}</p>
    const fmt = (d?: string | null) => (d ? formatDate(d, { dateStyle: 'short', timeStyle: 'short' }) : '—')
    const day = (d?: string) => (d ? formatDate(d, { dateStyle: 'long' }) : '—')

    const blockers = prep.blockers ?? []
    const reconvoke = (prep as { reconvoke_needed?: number }).reconvoke_needed ?? 0
    const steps: StepDef[] = [
        {
            id: 'preparation',
            title: t('convocation.preparation'),
            status: convened ? 'done' : blockers.length > 0 ? 'blocked' : 'todo',
            summary: convened
                ? t('convocation.summary_convened', { date: day(prep.remote_vote_last_day) })
                : blockers.length > 0
                  ? t(`convocation.blocker.${blockers[0]}`)
                  : t('convocation.summary_ready'),
            content: (
                <div className="space-y-3 text-sm" data-testid="convocation-preparation">
                    <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                        <dt className="text-muted-foreground">{t('convocation.held_at')}</dt>
                        <dd data-testid="convocation-held-at">{day(prep.held_at)}</dd>
                        <dt className="text-muted-foreground">{t('convocation.closes')}</dt>
                        <dd data-testid="convocation-closes">{day(prep.remote_vote_last_day)}</dd>
                        <dt className="text-muted-foreground">{t('convocation.modes')}</dt>
                        <dd className="flex flex-wrap gap-1" data-testid="convocation-modes">
                            {(prep.participation_modes ?? []).map((m) => (
                                <Badge key={m} variant="outline" data-testid={`convocation-mode-${m}`}>{t(`convocation.mode.${m}`)}</Badge>
                            ))}
                        </dd>
                        <dt className="text-muted-foreground">{t('convocation.electorate')}</dt>
                        <dd data-testid="convocation-electorate">
                            {t('convocation.electorate_value', { count: prep.electorate ?? 0, voices: prep.total_voices ?? 0 })}
                        </dd>
                    </dl>
                    {!prep.remote_means_decision_uid && (
                        <p className="flex items-center gap-1 text-muted-foreground" data-testid="convocation-no-video">
                            {t('convocation.no_video_short')}
                            <HelpHint testId="convocation-no-video-help">{t('convocation.no_video')}</HelpHint>
                        </p>
                    )}
                    <p data-testid="convocation-bureau">
                        {prep.agenda_starts_with_bureau ? t('convocation.bureau_ok') : t('convocation.bureau_missing')}
                    </p>
                    {blockers.length > 0 && (
                        <ul className="list-disc pl-5 text-destructive" data-testid="convocation-blockers">
                            {blockers.map((b) => <li key={b}>{t(`convocation.blocker.${b}`)}</li>)}
                        </ul>
                    )}
                    {(prep.anomalies ?? []).length > 0 && (
                        <div data-testid="convocation-anomalies">
                            <p className="font-medium">{t('convocation.anomalies')}</p>
                            <ul className="list-disc pl-5">
                                {(prep.anomalies ?? []).map((a, i) => (
                                    <li key={i} data-testid={`convocation-anomaly-${a.kind}`}>
                                        {a.user_name || a.asset_name || '—'} : {t(`convocation.anomaly.${a.kind}`)}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {prep.status === 'open' && reconvoke > 0 && (
                        <p className="text-destructive" data-testid="convocation-reconvoke">
                            {t('convocation.reconvoke', { count: reconvoke })}
                        </p>
                    )}
                    {prep.status === 'open' && (
                        <Can perm="contracts.write">
                            <Button size="sm" variant="outline" onClick={() => setPostponing(true)} data-testid="convocation-postpone">
                                {t('convocation.postpone')}
                            </Button>
                        </Can>
                    )}
                    {prep.status === 'draft' && (
                        <Can perm="contracts.write">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={prepare.isPending}
                                onClick={() => prepare.mutate({ params: { path } })}
                                data-testid="convocation-prepare"
                            >
                                {t('convocation.prepare')}
                            </Button>
                        </Can>
                    )}
                </div>
            ),
        },
        {
            id: 'participation',
            title: t('convocation.participation'),
            status: !convened ? 'blocked' : (participation?.ballots ?? []).length > 0 && (participation?.ballots ?? []).every((b) => !!b.last_submitted_at || b.proxy_given) ? 'done' : 'todo',
            summary: !convened
                ? t('convocation.after_convene')
                : t('convocation.summary.submitted', { count: participation?.summary?.submitted ?? 0, voices: participation?.summary?.submitted_voices ?? 0 }),
            content: !convened ? (
                <p className="text-sm text-muted-foreground" data-testid="convocation-participation-locked">{t('convocation.after_convene')}</p>
            ) : participation ? (
                <div data-testid="convocation-participation">
                    <div className="space-y-3">
                        <div className="flex flex-wrap gap-2 text-sm" data-testid="convocation-summary">
                            <Badge variant="outline" data-testid="convocation-summary-submitted">
                                {t('convocation.summary.submitted', { count: participation.summary?.submitted ?? 0, voices: participation.summary?.submitted_voices ?? 0 })}
                            </Badge>
                            <Badge variant="outline">{t('convocation.summary.in_person', { voices: participation.summary?.in_person_voices ?? 0 })}</Badge>
                            <Badge variant="outline">{t('convocation.summary.video', { voices: participation.summary?.video_voices ?? 0 })}</Badge>
                            <Badge variant="outline">{t('convocation.summary.mail_vote', { voices: participation.summary?.mail_vote_voices ?? 0 })}</Badge>
                            <Badge variant="outline">{t('convocation.summary.proxy', { voices: participation.summary?.proxy_voices ?? 0 })}</Badge>
                        </div>
                        {(participation.ballots ?? []).length === 0 ? (
                            <EmptyState title={t('convocation.no_ballot')} />
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="convocation-ballots">
                                    <thead className="text-left text-muted-foreground">
                                        <tr>
                                            <th className="py-1 pr-3">{t('convocation.col.co_owner')}</th>
                                            <th className="py-1 pr-3">{t('convocation.col.voices')}</th>
                                            <th className="py-1 pr-3">{t('convocation.col.channel')}</th>
                                            <th className="py-1 pr-3">{t('convocation.col.status')}</th>
                                            <th className="py-1 pr-3">{t('convocation.col.submitted')}</th>
                                            <th className="py-1" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(participation.ballots ?? []).map((b) => (
                                            <tr key={b.stakeholder_uid} className="border-t" data-testid={`convocation-ballot-${b.stakeholder_uid}`}>
                                                <td className="py-1.5 pr-3">{b.user_name}</td>
                                                <td className="py-1.5 pr-3">{b.voices}</td>
                                                <td className="py-1.5 pr-3">
                                                    {t(`convocation.link.${b.link_channel}`)}
                                                    {b.code_channel && <span className="text-muted-foreground"> · {t(`convocation.code.${b.code_channel}`)}</span>}
                                                </td>
                                                <td className="py-1.5 pr-3" data-testid="convocation-ballot-status">
                                                    {b.status === 'suspended' ? (
                                                        <Badge variant="warning">{t('convocation.suspended')}</Badge>
                                                    ) : b.proxy_given ? (
                                                        t('convocation.state.proxy')
                                                    ) : b.last_submitted_at ? (
                                                        t('convocation.state.submitted')
                                                    ) : b.participation_mode ? (
                                                        t(`convocation.mode.${b.participation_mode}`)
                                                    ) : b.opened_at ? (
                                                        t('convocation.state.opened')
                                                    ) : b.notified_at ? (
                                                        t('convocation.state.notified')
                                                    ) : (
                                                        t('convocation.state.not_notified')
                                                    )}
                                                </td>
                                                <td className="py-1.5 pr-3" data-testid="convocation-ballot-submitted">{fmt(b.last_submitted_at)}</td>
                                                <td className="py-1.5 text-right">
                                                    <Can perm="contracts.write">
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                disabled={reissue.isPending}
                                                                onClick={() => reissue.mutate({ params: { path: { ...path, stakeholderUid: b.stakeholder_uid! } } })}
                                                                data-testid={`convocation-reissue-${b.stakeholder_uid}`}
                                                            >
                                                                {b.status === 'suspended' ? t('convocation.validate') : t('convocation.reissue')}
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => {
                                                                    setLetterError(null)
                                                                    downloadLetter(org, minutesUid, b.stakeholder_uid!).catch(() => setLetterError(t('convocation.letter_error')))
                                                                }}
                                                                data-testid={`convocation-letter-${b.stakeholder_uid}`}
                                                            >
                                                                {t('convocation.letter')}
                                                            </Button>
                                                        </div>
                                                    </Can>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            ) : null,
        },
        {
            id: 'journal',
            title: t('convocation.journal'),
            status: !convened ? 'blocked' : 'done',
            summary: !convened ? t('convocation.after_convene') : journal?.verification?.valid ? t('convocation.journal_valid') : undefined,
            content: !convened ? (
                <p className="text-sm text-muted-foreground" data-testid="convocation-journal-locked">{t('convocation.after_convene')}</p>
            ) : journal ? (
                <div data-testid="convocation-journal" className="space-y-3">
                    <Badge variant={journal.verification?.valid ? 'outline' : 'destructive'} data-testid="convocation-journal-verification">
                        {journal.verification?.valid ? t('convocation.journal_valid') : t('convocation.journal_broken', { sequence: journal.verification?.broken_at })}
                    </Badge>

                        <ol className="space-y-1 text-sm">
                            {(journal.data ?? []).map((e) => (
                                <li key={e.uid} className="flex gap-3" data-testid={`convocation-event-${e.kind}`}>
                                    <span className="w-8 text-right text-muted-foreground">{e.sequence}</span>
                                    <span className="w-36 text-muted-foreground">{fmt(e.created_at)}</span>
                                    <span>{t(`convocation.event.${e.kind}`, { defaultValue: e.kind })}</span>
                                </li>
                            ))}
                        </ol>
                                    </div>
            ) : null,
        },
    ]
    const auto = convened ? 'participation' : currentStep(steps)
    const open = chosen === null ? auto : chosen

    return (
        <div className="space-y-4" data-testid="convocation-tab">
            <Can perm="contracts.write">
                {prep.status === 'draft' ? (
                    <NextAction
                        label={t('steps.next_action')}
                        hint={blockers.length > 0 ? t(`convocation.blocker.${blockers[0]}`) : t('convocation.next_convene', { count: prep.electorate ?? 0 })}
                        testId="convocation-next-action"
                    >
                        <Button
                            disabled={blockers.length > 0 || convene.isPending}
                            onClick={() => setConfirm(true)}
                            data-testid="convocation-convene"
                        >
                            {t('convocation.convene')}
                        </Button>
                    </NextAction>
                ) : (
                    <NextAction label={t('steps.next_action')} hint={t('convocation.next_remind')} testId="convocation-next-action">
                        <Button
                            disabled={remind.isPending}
                            onClick={() =>
                                remind.mutate(
                                    { params: { path }, body: {} },
                                    { onSuccess: (r) => setNotice(t('convocation.reminded', { count: r?.sent ?? 0 })) },
                                )
                            }
                            data-testid="convocation-remind"
                        >
                            {t('convocation.remind')}
                        </Button>
                    </NextAction>
                )}
            </Can>

            {error && <p className="text-sm text-destructive" data-testid="convocation-error">{error}</p>}
            {notice && <p className="text-sm text-muted-foreground" data-testid="convocation-notice">{notice}</p>}

            <StepList steps={steps} open={open} onOpenChange={setChosen} testId="convocation-steps" />

            <Dialog
                open={postponing}
                onOpenChange={(o) => {
                    setPostponing(o)
                    if (!o) postpone.reset()
                }}
            >
                <DialogContent data-testid="postpone-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('convocation.postpone_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{t('convocation.postpone_help', { date: day(prep.held_at) })}</p>
                        <div>
                            <Label htmlFor="postpone-date">{t('convocation.postpone_date')}</Label>
                            <Input id="postpone-date" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="postpone-date" />
                        </div>
                        <div>
                            <Label htmlFor="postpone-reason">{t('convocation.postpone_reason')}</Label>
                            <Input id="postpone-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="postpone-reason" />
                        </div>
                        {postponeError && <p className="text-sm text-destructive" data-testid="postpone-error">{postponeError}</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPostponing(false)}>{t('cancel')}</Button>
                        <Button
                            disabled={!newDate || !reason.trim() || postpone.isPending}
                            onClick={() =>
                                postpone.mutate(
                                    { params: { path }, body: { held_at: newDate, reason: reason.trim() } as never },
                                    { onSuccess: () => { setPostponing(false); setNewDate(''); setReason('') } },
                                )
                            }
                            data-testid="postpone-confirm"
                        >
                            {t('convocation.postpone')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirm} onOpenChange={setConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('convocation.confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('convocation.confirm_body', { count: prep.electorate ?? 0, date: day(prep.remote_vote_last_day) })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <label className="flex items-center gap-2 text-sm">
                        {t('convocation.identity_level')}
                        <select
                            value={identityLevel}
                            onChange={(e) => setIdentityLevel(e.target.value as 'otp' | 'strong')}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="convocation-identity-level"
                        >
                            <option value="otp">{t('convocation.level.otp')}</option>
                            <option value="strong">{t('convocation.level.strong')}</option>
                        </select>
                    </label>
                    <div className="space-y-1 text-sm" data-testid="convocation-postal">
                        <p className="font-medium">{t('convocation.postal_title')}</p>
                        <p className="flex items-center gap-1 text-muted-foreground">
                            {t('convocation.postal_help_short')}
                            <HelpHint testId="convocation-postal-help">{t('convocation.postal_help')}</HelpHint>
                        </p>
                        {(sheet?.parties ?? []).length === 0 ? (
                            <p className="text-muted-foreground" data-testid="convocation-postal-empty">{t('convocation.postal_empty')}</p>
                        ) : (
                            <ul className="max-h-48 space-y-1 overflow-y-auto">
                                {(sheet?.parties ?? []).map((p) => (
                                    <li key={p.stakeholder_uid}>
                                        <label className="flex items-center gap-2">
                                            <Checkbox
                                                checked={postal.includes(p.stakeholder_uid)}
                                                onCheckedChange={(checked) =>
                                                    setPostal((cur) => (checked ? [...cur, p.stakeholder_uid] : cur.filter((u) => u !== p.stakeholder_uid)))
                                                }
                                                data-testid={`convocation-postal-${p.stakeholder_uid}`}
                                            />
                                            {p.user_name || '—'}
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="convocation-confirm-cancel">{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="convocation-confirm"
                            onClick={() =>
                                convene.mutate(
                                    { params: { path }, body: { identity_level: identityLevel, postal_stakeholder_uids: postal } },
                                    { onSuccess: (r) => setNotice(t('convocation.convened', { electronic: r?.electronic ?? 0, postal: r?.postal ?? 0 })) },
                                )
                            }
                        >
                            {t('convocation.convene')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
